import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from './services/api';

// UI Components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Alert, AlertDescription } from './components/ui/alert';
import { Separator } from './components/ui/separator';

// Icons
import {
  DollarSign, Target, AlertCircle, TrendingUp, BarChart3, ShoppingCart,
  Plus, Bell, Home, User, Star, Settings, ChevronRight, XCircle, Users,
  Menu, X, Activity, RefreshCw, CreditCard, Zap, Sun, Moon, Trophy,
  CandlestickChart as CandlestickIcon
} from 'lucide-react';

// Toast notifications
import { toast, Toaster } from 'sonner';

// Charts
import { 
  XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, ComposedChart, ReferenceLine,
  Area, Bar
} from 'recharts';

// Animation
import { motion, AnimatePresence } from 'framer-motion';

// Constants - now imported from centralized location
import { API } from './constants';

import LeaderboardPanel from './components/LeaderboardPanel';
import ScrollToTopButton from './components/ScrollToTopButton';
import AgbPage from './pages/legal/AgbPage';
import DatenschutzPage from './pages/legal/DatenschutzPage';
import ImpressumPage from './pages/legal/ImpressumPage';
import Footer from './components/shared/Footer';

// Pages - imported from centralized location
import { 
  HomePage, LoginPage, RegisterPage, MarketsPage, 
  PortfolioPage, ShopPage, ProfilePage, CreatorDashboard,
  AdminPage
} from './pages';

// Contexts - imported from centralized location
import { AuthProvider, useAuth } from './contexts';
import { WebSocketProvider, useWebSocketContext } from './contexts';
import { ThemeProvider, useTheme } from './contexts';

// Hooks and utilities
import { useOnlineStatus } from './hooks';
import { hapticFeedback } from './utils';

// Configure axios defaults (handled in services/api.js)



// Service Worker Registration
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('SW registered:', registration.scope);
      return registration;
    } catch (error) {
      console.log('SW registration failed:', error);
    }
  }
  return null;
};

// Push Notification Subscription
const subscribeToPushNotifications = async () => {
  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      // Generate a VAPID key (in production, use server-side generated key)
      const vapidPublicKey = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';
      
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
    }
    
    // Send subscription to server
    await api.push.subscribe(subscription.toJSON());
    return true;
  } catch (error) {
    console.log('Push subscription error:', error);
    return false;
  }
};

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Custom Hook for Pull to Refresh
const usePullToRefresh = (onRefresh, options = {}) => {
  const { threshold = 80, enabled = true } = options;
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const containerRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (!enabled || window.scrollY > 0) return;
    startY.current = e.touches[0].clientY;
    setIsPulling(true);
  }, [enabled]);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || !enabled) return;
    const currentY = e.touches[0].clientY;
    const distance = Math.max(0, Math.min(currentY - startY.current, threshold * 1.5));
    setPullDistance(distance);
  }, [isPulling, enabled, threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) return;
    
    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true);
      hapticFeedback('medium');
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
    
    setIsPulling(false);
    setPullDistance(0);
  }, [isPulling, pullDistance, threshold, isRefreshing, onRefresh]);

  return {
    containerRef,
    pullDistance,
    isRefreshing,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd
    }
  };
};

// Skeleton Loading Component
const Skeleton = ({ className = '', variant = 'text' }) => {
  const baseClass = 'animate-pulse bg-gray-200 rounded';
  const variants = {
    text: 'h-4 w-full',
    title: 'h-6 w-3/4',
    avatar: 'h-12 w-12 rounded-full',
    card: 'h-32 w-full',
    button: 'h-10 w-24'
  };
  return <div className={`${baseClass} ${variants[variant] || ''} ${className}`} />;
};

// Card Skeleton for loading states
const CardSkeleton = () => (
  <Card className="animate-pulse">
    <CardContent className="pt-6">
      <Skeleton variant="title" className="mb-4" />
      <Skeleton className="mb-2" />
      <Skeleton className="mb-2 w-2/3" />
      <div className="flex gap-2 mt-4">
        <Skeleton variant="button" />
        <Skeleton variant="button" />
      </div>
    </CardContent>
  </Card>
);

// Candlestick Chart Component
const PriceCandlestickChart = ({ data, title, color = '#10b981' }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
        No price history available
      </div>
    );
  }

  return (
    <div>
      <h4 className="font-semibold mb-3 flex items-center gap-2">
        <CandlestickIcon className="h-4 w-4" />
        {title}
      </h4>
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 10 }}
            tickFormatter={(val) => val.slice(5)}
          />
          <YAxis 
            domain={[0, 1]} 
            tick={{ fontSize: 10 }}
            tickFormatter={(val) => `${(val * 100).toFixed(0)}%`}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const d = payload[0].payload;
                return (
                  <div className="bg-white p-3 rounded-lg shadow-lg border text-sm dark:bg-slate-900/80">
                    <p className="font-medium">{d.date}</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                      <span className="text-gray-500 dark:text-gray-400">Open:</span>
                      <span>{(d.open * 100).toFixed(1)}%</span>
                      <span className="text-gray-500 dark:text-gray-400">High:</span>
                      <span className="text-green-600">{(d.high * 100).toFixed(1)}%</span>
                      <span className="text-gray-500 dark:text-gray-400">Low:</span>
                      <span className="text-red-600 dark:text-rose-400">{(d.low * 100).toFixed(1)}%</span>
                      <span className="text-gray-500 dark:text-gray-400">Close:</span>
                      <span className="font-medium">{(d.close * 100).toFixed(1)}%</span>
                      <span className="text-gray-500 dark:text-gray-400">Volume:</span>
                      <span>{d.volume?.toLocaleString() || 0}</span>
                    </div>
                  </div>
                );
              }
              return null;
            }}
          />
          <Bar dataKey="volume" fill="#e5e7eb" opacity={0.3} yAxisId="volume" />
          <ReferenceLine y={0.5} stroke="#9ca3af" strokeDasharray="3 3" />
          <Area
            type="monotone"
            dataKey="close"
            stroke={color}
            fill={color}
            fillOpacity={0.1}
            strokeWidth={2}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};




// Avatar + Name + Cosmetic-Chips für den Header-Profile-Button.
// Rendert die equipped Items aus dem Virtual Shop (Avatar-Emoji,
// Profile-Frame-Farbe, Name-Color, Custom-Title, Pro-Badge,
// Animated-Frame). Ohne Items fällt es auf den Standard-User-Icon
// und Default-Stil zurück.
const FRAME_RING_CLASS = {
  green: 'ring-2 ring-emerald-500',
  blue: 'ring-2 ring-blue-500',
  red: 'ring-2 ring-red-500',
  gold: 'ring-2 ring-yellow-500',
};
const NAME_COLOR_CLASS = {
  gold: 'text-yellow-500 font-semibold',
  silver: 'text-gray-400 font-semibold',
  neon: 'text-pink-500 font-semibold drop-shadow-[0_0_4px_rgba(236,72,153,0.6)]',
};

const ProfileBadgeChrome = ({ equipped = {}, handle }) => {
  const frame = equipped.animated_frame?.variant || equipped.profile_frame?.variant;
  const frameClass = FRAME_RING_CLASS[frame] || '';
  const animated = !!equipped.animated_frame?.variant;
  const nameColorClass = NAME_COLOR_CLASS[equipped.name_color?.variant] || '';
  const emoji = equipped.avatar_emoji?.variant;

  return (
    <>
      {emoji ? (
        <span
          className={`inline-flex items-center justify-center text-lg leading-none w-7 h-7 rounded-full bg-white ${frameClass} ${animated ? 'animate-pulse' : ''}`}
          title={equipped.avatar_emoji.item_name}
        >
          {emoji}
        </span>
      ) : frame ? (
        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 ${frameClass} ${animated ? 'animate-pulse' : ''}`}>
          <Users className="h-4 w-4" />
        </span>
      ) : (
        <Users className="h-4 w-4" />
      )}
      <span className={nameColorClass}>{handle}</span>
      {equipped.pro_badge && (
        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-yellow-400 to-amber-500 text-white rounded">
          PRO
        </span>
      )}
      {equipped.custom_title?.variant && (
        <span className={`px-1.5 py-0.5 text-[10px] rounded ${
          equipped.custom_title.tier === 'gold' ? 'bg-amber-100 text-amber-800'
          : equipped.custom_title.tier === 'silver' ? 'bg-gray-200 text-gray-700'
          : 'bg-orange-100 text-orange-700'
        }`}>
          {equipped.custom_title.variant}
        </span>
      )}
    </>
  );
};

// Components
const Header = () => {
  const { t } = useTranslation();
  // AuthContext now owns the credit balance — single source of truth so
  // every consumer (Header, Shop, BetDialog, …) updates after each
  // refreshCredits() call. The notification-related fields are
  // defaulted because the provider hasn't wired the notifications
  // feature through yet.
  const auth = useAuth() || {};
  const {
    user,
    logout,
    credits = 0,
    refreshCredits = () => {},
    notifications = [],
    notificationCount = 0,
    fetchNotifications = () => {},
    markNotificationAsRead = () => {},
    isAdmin = false,
    isPlayer = false,
    cosmetics = {},
  } = auth;
  const ws = useWebSocketContext() || {};
  const {
    wsConnected = false,
    liveNotificationCount = 0,
    authenticateUser = () => {},
    resetNotificationCount = () => {},
  } = ws;
  const { isDark, toggleTheme } = useTheme();
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  // Cosmetics kommen jetzt aus AuthContext, damit Shop-Aktionen den
  // Header live aktualisieren können.
  const equippedCosmetics = cosmetics;
  // Header-Dropdown zeigt nur ungelesene Notifications — sobald der
  // User auf eine klickt und sie als gelesen markiert ist, fliegt sie
  // raus. Die Vollliste sieht der User auf der Profil-Seite.
  const unreadNotifications = notifications.filter((n) => !n.read);
  const navigate = useNavigate();
  // Admins haben Notifications direkt im Admin-Panel — Bell und
  // "View All" springen dort hin statt ins Profil/Dropdown.
  const notificationsPath = isAdmin ? '/admin?tab=notifications' : '/profile';

  // Authenticate user with WebSocket when user logs in
  useEffect(() => {
    if (user?.id && wsConnected) {
      authenticateUser(user.id);
    }
  }, [user?.id, wsConnected, authenticateUser]);

  // Use live notification count from WebSocket, fallback to polled count
  const displayNotificationCount = liveNotificationCount > 0 ? liveNotificationCount : notificationCount;

  // Sync notification count when user opens notifications dropdown.
  // Admins überspringen das Dropdown und springen direkt in den
  // Notifications-Tab im Admin-Panel.
  const handleNotificationClick = () => {
    if (isAdmin) {
      navigate('/admin?tab=notifications');
      return;
    }
    setShowNotifications(!showNotifications);
    if (!showNotifications) {
      fetchNotifications();
      // Burger-Menü schließen, damit die beiden Overlays sich auf Mobile
      // nicht überlagern (Burger schob sich sonst hinter die Notifications).
      setShowMobileMenu(false);
    }
  };

  // Reset live count when notifications are viewed
  useEffect(() => {
    if (showNotifications && liveNotificationCount > 0) {
      // After viewing notifications, sync the count
      resetNotificationCount(notificationCount);
    }
  }, [showNotifications, liveNotificationCount, notificationCount, resetNotificationCount]);

  // Local helper: nach einem Mark-Read explizit den Live-Counter
  // nachziehen. Vorher hatten wir hier einen Effect, der bei jedem
  // `liveNotificationCount > notificationCount` gefeuert hat — das hat
  // aber auch echte neue WebSocket-Pushes sofort wieder weggewischt,
  // weil der lokale Count noch nicht nachgezogen war. Jetzt explizit
  // an der Stelle, wo wir wissen dass der User read hat.
  const markReadAndSyncLive = (id) => {
    markNotificationAsRead(id);
    if (liveNotificationCount > 0) {
      resetNotificationCount(Math.max(0, liveNotificationCount - 1));
    }
  };

  // Notification-Dropdown + Mobile-Menü schließen, wenn außerhalb des
  // Headers geklickt/getippt wird oder Escape gedrückt wird. Beide leben
  // im <header>, daher reicht closest('header') (robuster als der frühere
  // .relative-Selektor, der auf jeder beliebigen relative-Box ansprang).
  useEffect(() => {
    if (!showNotifications && !showMobileMenu) return;
    const handlePointerOutside = (event) => {
      if (!event.target.closest('header')) {
        setShowNotifications(false);
        setShowMobileMenu(false);
      }
    };
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setShowNotifications(false);
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handlePointerOutside);
    document.addEventListener('touchstart', handlePointerOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handlePointerOutside);
      document.removeEventListener('touchstart', handlePointerOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [showNotifications, showMobileMenu]);

  // Credits are loaded by AuthContext on login / mount and by callers
  // of refreshCredits() after credit-changing actions.

  const handlePurchaseSuccess = () => {
    // Pull the fresh balance from the server. Callbacks that pass
    // newBalance directly are still tolerated — they just trigger one
    // extra round-trip that confirms the server-side number.
    refreshCredits();
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/40 dark:bg-slate-950/30 backdrop-blur-xl border-b border-white/40 dark:border-white/10">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center space-x-3">
            {/* Logo target depends on auth state: anonymous users
                land on the marketing homepage, logged-in users skip
                straight into the markets list. Wortmarke wird auf sehr
                schmalen Screens (<390px) ausgeblendet, damit der Header
                nicht überläuft -- das Icon bleibt als Brand-Mark. */}
            <Link to={user ? '/markets' : '/'} className="flex items-center space-x-2 shrink-0">
              <Target className="h-7 w-7 md:h-8 md:w-8 shrink-0 text-blue-800 dark:text-blue-300" />
              <span className="hidden min-[390px]:inline text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-700 to-blue-900 dark:from-blue-200 dark:to-white bg-clip-text text-transparent">
                PredFuture
              </span>
            </Link>
            {/* Real-time Connection Status */}
            <div 
              className={`hidden sm:flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                wsConnected
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400'
                  : 'bg-gray-100 text-gray-500 dark:bg-slate-800 dark:text-slate-400'
              }`}
              title={wsConnected ? 'Live updates active' : 'Reconnecting...'}
            >
              <Activity className={`h-3 w-3 ${wsConnected ? 'animate-pulse' : ''}`} />
              <span className="hidden md:inline">{wsConnected ? 'Live' : t('errors.offline')}</span>
            </div>
          </div>
          
          {/* Desktop Navigation — Portfolio/Shop/Creator are
              player-only sections. Admin operators see only Markets +
              Admin. */}
          <nav className="hidden md:flex items-center space-x-6 ml-12">
            <Link to="/markets" className="text-gray-700 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors flex items-center gap-1 dark:text-gray-300">
              <span className="text-sm">{t('nav.markets')}</span>
            </Link>
            {isPlayer && (
              <>
                <Link to="/portfolio" className="text-gray-700 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors dark:text-gray-300">
                  {t('nav.portfolio')}
                </Link>
                <Link to="/shop" className="text-gray-700 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors dark:text-gray-300">
                  {t('nav.shop')}
                </Link>
                <Link to="/creator" className="text-gray-700 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors dark:text-gray-300">
                  {t('nav.creator')}
                </Link>
              </>
            )}
            {isAdmin && (
              <Link to="/admin" className="text-gray-700 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors dark:text-gray-300">
                Admin
              </Link>
            )}
          </nav>

          {/* Desktop User Actions */}
          <div className="hidden md:flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              aria-label={isDark ? 'Light Mode aktivieren' : 'Dark Mode aktivieren'}
              title={isDark ? 'Light Mode' : 'Dark Mode'}
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            {user ? (
              <>
                {/* Credits chip is player-only — admins don't have a
                    spendable balance. */}
                {isPlayer && (
                  <div
                    className="flex items-center space-x-2 bg-blue-50 px-3 py-1 rounded-full cursor-pointer hover:bg-blue-100 transition-colors dark:bg-blue-500/10"
                    onClick={() => setShowPurchaseDialog(true)}
                    title="Click to buy more credits"
                  >
                    <span className="font-semibold text-blue-900 dark:text-blue-200">{credits.toLocaleString()} Credits</span>
                    <Plus className="h-3 w-3 text-blue-700 dark:text-blue-300" />
                  </div>
                )}
                
                {/* Notification Bell */}
                <div className="relative">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleNotificationClick}
                    className="relative"
                  >
                    <Bell className={`h-4 w-4 ${displayNotificationCount > 0 ? 'text-blue-600' : ''}`} />
                    {displayNotificationCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                        {displayNotificationCount > 99 ? '99+' : displayNotificationCount}
                      </span>
                    )}
                  </Button>
                  
                  {/* Notification Dropdown */}
                  {showNotifications && (
                    <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto dark:bg-slate-900/80 dark:border-slate-700">
                      <div className="p-3 border-b border-gray-100 dark:border-slate-800">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
                      </div>
                      
                      {unreadNotifications.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                          Keine ungelesenen Notifications
                        </div>
                      ) : (
                        <div className="max-h-64 overflow-y-auto">
                          {unreadNotifications.map((notification) => (
                            <div 
                              key={notification.id} 
                              className={`p-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors ${
                                notification.read ? 'opacity-60' : ''
                              }`}
                              onClick={() => markReadAndSyncLive(notification.id)}
                            >
                              <div className="flex items-start space-x-3">
                                <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                                  notification.read ? 'bg-gray-300' : (
                                    notification.priority === 'high' ? 'bg-red-400' : 
                                    notification.priority === 'medium' ? 'bg-yellow-400' : 'bg-blue-400'
                                  )
                                }`} />
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium ${
                                    notification.read ? 'text-gray-600' : 'text-gray-900'
                                  }`}>
                                    {notification.title}
                                  </p>
                                  <p className={`text-sm mt-1 ${
                                    notification.read ? 'text-gray-500' : 'text-gray-600'
                                  }`}>
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
                                    {new Date(notification.created_at).toLocaleString()}
                                  </p>
                                </div>
                                {!notification.read && (
                                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2"></div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="p-2 border-t border-gray-100 dark:border-slate-800">
                        <Link to={notificationsPath} onClick={() => setShowNotifications(false)}>
                          <Button variant="ghost" size="sm" className="w-full text-sm">
                            View All Notifications
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
                
                <Link to="/profile">
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    <ProfileBadgeChrome equipped={equippedCosmetics} handle={user?.handle || 'Profile'} />
                  </Button>
                </Link>
                <Button variant="ghost" onClick={logout}>
                  {t('nav.logout')}
                </Button>
              </>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login">
                  <Button variant="ghost">{t('nav.login')}</Button>
                </Link>
                <Link to="/register">
                  <Button>{t('nav.signup')}</Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile User Actions */}
          <div className="flex md:hidden items-center space-x-2 shrink-0">
            {user ? (
              <>
                {/* Mobile Credits Display — player-only (admins
                    have no spendable balance). */}
                {isPlayer && (
                  <div
                    className="flex items-center space-x-1 bg-blue-50 px-2 py-1 rounded-full cursor-pointer dark:bg-blue-500/10"
                    onClick={() => setShowPurchaseDialog(true)}
                    title="Buy credits"
                  >
                    <span className="text-xs font-semibold text-blue-900 dark:text-blue-200">{credits.toLocaleString()}</span>
                    <Plus className="h-2 w-2 text-blue-700 dark:text-blue-300" />
                  </div>
                )}
                
                {/* Mobile Notification Bell */}
                <div className="relative">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleNotificationClick}
                    className="relative p-2"
                  >
                    <Bell className={`h-4 w-4 ${displayNotificationCount > 0 ? 'text-blue-600' : ''}`} />
                    {displayNotificationCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center animate-pulse">
                        {displayNotificationCount > 9 ? '9+' : displayNotificationCount}
                      </span>
                    )}
                  </Button>
                  
                  {/* Mobile Notification Dropdown — fixed unter dem Header
                      und über die volle Breite (left/right-2) statt am Bell
                      verankert, sonst lief das w-72-Panel auf schmalen Screens
                      links aus dem Viewport. Solider Hintergrund für Lesbarkeit. */}
                  {showNotifications && (
                    <div className="fixed left-2 right-2 top-16 w-auto bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-[70vh] overflow-y-auto dark:bg-slate-900 dark:border-slate-700">
                      <div className="p-3 border-b border-gray-100 dark:border-slate-800">
                        <h3 className="font-semibold text-gray-900 text-sm dark:text-gray-100">Notifications</h3>
                      </div>
                      
                      {unreadNotifications.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 text-sm dark:text-gray-400">
                          Keine ungelesenen Notifications
                        </div>
                      ) : (
                        <div className="max-h-60 overflow-y-auto">
                          {unreadNotifications.map((notification) => (
                            <div
                              key={notification.id}
                              className={`p-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors dark:border-slate-800 dark:hover:bg-slate-800/60 ${
                                notification.read ? 'opacity-60' : ''
                              }`}
                              onClick={() => markReadAndSyncLive(notification.id)}
                            >
                              <div className="flex items-start space-x-2">
                                <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                                  notification.read ? 'bg-gray-300' : (
                                    notification.priority === 'high' ? 'bg-red-400' :
                                    notification.priority === 'medium' ? 'bg-yellow-400' : 'bg-blue-400'
                                  )
                                }`} />
                                <div className="flex-1 min-w-0">
                                  <p className={`text-xs font-medium ${
                                    notification.read ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'
                                  }`}>
                                    {notification.title}
                                  </p>
                                  <p className={`text-xs mt-1 ${
                                    notification.read ? 'text-gray-500 dark:text-gray-500' : 'text-gray-600 dark:text-gray-300'
                                  }`}>
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
                                    {new Date(notification.created_at).toLocaleString()}
                                  </p>
                                </div>
                                {!notification.read && (
                                  <div className="w-1 h-1 bg-blue-500 rounded-full flex-shrink-0 mt-1"></div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="p-2 border-t border-gray-100 dark:border-slate-800">
                        <Link to={notificationsPath} onClick={() => setShowNotifications(false)}>
                          <Button variant="ghost" size="sm" className="w-full text-xs">
                            View All Notifications
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

                {/* Mobile Menu Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setShowMobileMenu(!showMobileMenu); setShowNotifications(false); }}
                  className="p-2"
                >
                  <motion.div
                    animate={{ rotate: showMobileMenu ? 90 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {showMobileMenu ? (
                      <X className="h-5 w-5" />
                    ) : (
                      <Menu className="h-5 w-5" />
                    )}
                  </motion.div>
                </Button>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm">{t('nav.login')}</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm">{t('nav.signup')}</Button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Animated Mobile Menu */}
        <AnimatePresence>
          {showMobileMenu && user && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="md:hidden overflow-hidden"
            >
              <div className="pt-4 pb-2 border-t border-gray-200 mt-4 dark:border-slate-700">
                <nav className="flex flex-col space-y-1">
                  {[
                    { to: '/markets', label: 'Markets', icon: TrendingUp },
                    // Player-only sections — admins skip these.
                    ...(isPlayer ? [
                      { to: '/portfolio', label: 'Portfolio', icon: BarChart3 },
                      { to: '/shop', label: 'Shop', icon: ShoppingCart },
                      { to: '/creator', label: 'Creator', icon: Star },
                    ] : []),
                    { to: '/profile', label: user?.handle || 'Profile', icon: User },
                    ...(isAdmin ? [{ to: '/admin', label: 'Admin Panel', icon: Settings }] : []),
                  ].map((item, index) => (
                    <motion.div
                      key={item.to}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link 
                        to={item.to}
                        className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-all dark:text-gray-300 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-400"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        <item.icon className="h-5 w-5" />
                        <span className="font-medium">{item.label}</span>
                        <ChevronRight className="h-4 w-4 ml-auto text-gray-400 dark:text-gray-500" />
                      </Link>
                    </motion.div>
                  ))}
                  {/* Leaderboard — auf Mobile gibt es keinen Floating-Button
                      mehr (der überlappte Content), darum hier öffnbar. Feuert
                      ein window-Event, auf das LeaderboardPanel lauscht. */}
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.22 }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        window.dispatchEvent(new Event('leaderboard:open'));
                        setShowMobileMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-all dark:text-gray-300 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-400"
                    >
                      <Trophy className="h-5 w-5" />
                      <span className="font-medium">Leaderboard</span>
                      <ChevronRight className="h-4 w-4 ml-auto text-gray-400 dark:text-gray-500" />
                    </button>
                  </motion.div>
                  {/* Theme-Toggle — auf Mobile gibt es keine Header-Leiste
                      mit dem Moon/Sun-Button, also hier ins Menü. Schließt
                      das Menü bewusst nicht, damit der Wechsel sofort sichtbar
                      ist und man zurückschalten kann. */}
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.25 }}
                  >
                    <button
                      type="button"
                      onClick={toggleTheme}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-all dark:text-gray-300 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-400"
                    >
                      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                      <span className="font-medium">{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>
                  </motion.div>
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Separator className="my-2" />
                    <Button
                      variant="ghost"
                      onClick={() => {
                        logout();
                        setShowMobileMenu(false);
                      }}
                      className="w-full justify-start gap-3 px-3 py-3 h-auto text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-rose-400 dark:hover:bg-rose-500/10 dark:hover:text-rose-400"
                    >
                      <XCircle className="h-5 w-5" />
                      <span className="font-medium">{t('nav.logout')}</span>
                    </Button>
                  </motion.div>
                </nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Credit Purchase Dialog */}
      {showPurchaseDialog && (
        <CreditPurchaseDialog
          isOpen={showPurchaseDialog}
          onClose={() => setShowPurchaseDialog(false)}
          onPurchaseSuccess={handlePurchaseSuccess}
        />
      )}
    </header>
  );
};

// Mobile Bottom Navigation Component
const BottomNavigation = () => {
  const location = useLocation();
  const { user, isAdmin, isPlayer } = useAuth();
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const navigate = useNavigate();

  // Swipe detection
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    const navItems = !user
      ? ['/markets', '/login']
      : isAdmin
      ? ['/markets', '/admin', '/profile']
      : ['/markets', '/portfolio', '/shop', '/creator', '/profile'];
    const currentIndex = navItems.indexOf(location.pathname);
    
    if (isLeftSwipe && currentIndex < navItems.length - 1) {
      navigate(navItems[currentIndex + 1]);
    }
    if (isRightSwipe && currentIndex > 0) {
      navigate(navItems[currentIndex - 1]);
    }
  };

  if (!user) return null;

  // Logged-in users skip the marketing homepage entirely — Markets is
  // their landing page. Players see the four core sections; admin
  // operators see only Markets + Profile (Admin Panel is reachable
  // from the desktop nav, mobile admin work happens on desktop).
  const navItems = isAdmin
    ? [
        { path: '/markets', icon: TrendingUp, label: 'Markets' },
        { path: '/admin', icon: Settings, label: 'Admin' },
        { path: '/profile', icon: User, label: 'Profile' },
      ]
    : [
        { path: '/markets', icon: TrendingUp, label: 'Markets' },
        { path: '/shop', icon: ShoppingCart, label: 'Shop' },
        { path: '/portfolio', icon: BarChart3, label: 'Portfolio' },
        { path: '/profile', icon: User, label: 'Profile' },
      ];

  return (
    <motion.nav 
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      className="md:hidden fixed bottom-0 left-0 right-0 bg-white/90 dark:bg-slate-950/90 backdrop-blur-md border-t border-gray-200 dark:border-slate-800 z-50 safe-area-bottom shadow-lg dark:border-slate-700"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex justify-around items-center py-2 px-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex flex-col items-center py-1 px-3 min-w-0"
            >
              <motion.div
                whileTap={{ scale: 0.9 }}
                className={`p-2 rounded-xl transition-colors ${
                  isActive
                    ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400'
                }`}
              >
                <Icon className="h-5 w-5" />
              </motion.div>
              <span className={`text-xs mt-1 truncate ${
                isActive
                  ? 'text-emerald-600 dark:text-emerald-400 font-medium'
                  : 'text-gray-500 dark:text-gray-400'
              }`}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </motion.nav>
  );
};


import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import CreditPurchaseDialog from './components/shared/CreditPurchaseDialog';

// Referral Tracker - picks up ?ref= from URL and stores in localStorage
const ReferralTracker = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref && /^[A-Za-z0-9_]{3,30}$/.test(ref)) {
      localStorage.setItem('ref_code', ref);
      params.delete('ref');
      const newSearch = params.toString();
      window.history.replaceState({}, '', `${location.pathname}${newSearch ? '?' + newSearch : ''}`);
    }
  }, [location.search, location.pathname]);
  return null;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <WebSocketProvider>
        <ErrorBoundary>
          <div className="App min-h-screen pb-20 md:pb-0 bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950">
            <BrowserRouter>
              <ReferralTracker />
              <Header />
              <LeaderboardPanel />
              <ScrollToTopButton />
              {/* pt-[61px] kompensiert den fixed-position-Header, damit
                  Routen-Content nicht unter dem Header verschwindet.
                  Header ist fixed (statt sticky) damit der HomePage-
                  Gradient unter dem Header durchlaeuft -- backdrop-blur
                  blurrt dann die Hero-Gradient-Farbe statt Body-BG. */}
              <main className="pt-[61px]">
              <Routes>
              <Route path="/" element={<HomePage />} />
              {/* /signup ist KEINE eigene Seite -- Alias auf die Landing,
                  damit geteilte ?ref=-Links (predfuture.com/signup?ref=...)
                  nicht ins Leere laufen. Der ReferralTracker liest ?ref
                  hier aus. Kanonischer Link zeigt jetzt auf "/". */}
              <Route path="/signup" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/markets" element={<MarketsPage />} />
              {/* Deep-link a single market: same page, scroll the
                  matching card into view. The list IS the detail UI. */}
              <Route path="/markets/:marketId" element={<MarketsPage />} />
              <Route path="/civic" element={<Navigate to="/markets" replace />} />
              <Route path="/portfolio" element={<ProtectedRoute><PortfolioPage /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/shop" element={<ProtectedRoute><ShopPage /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute requireRole="admin"><AdminPage /></ProtectedRoute>} />
              {/* Öffentlich: Logged-out sehen die „Creator werden"-Pitch
                  (Vorteile + Commission); Apply/Dashboard erfordern Login
                  (in der Komponente gehandhabt). */}
              <Route path="/creator" element={<CreatorDashboard />} />
              <Route path="/agb" element={<AgbPage />} />
              <Route path="/datenschutz" element={<DatenschutzPage />} />
              <Route path="/impressum" element={<ImpressumPage />} />
            </Routes>
            <Footer />
            </main>
            <BottomNavigation />
          </BrowserRouter>
          <Toaster position="top-right" />
        </div>
        </ErrorBoundary>
        </WebSocketProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;